import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { Drawer, Spotlight, Typography, useIntentBehavior } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useVisibleNotifications } from '~/components/InAppNotificationsDrawer/hooks/useVisibleNotifications';
import { getNotificationPresentationData } from '~/components/InAppNotificationsDrawer/InAppNotificationsDrawer.utils';
import { NOTIFICATIONS_TRIGGER_SPOTLIGHT_ID } from '~/components/NavigationAndStructure/InAppNotificationsButton/InAppNotificationsButton';
import { useIoContext } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { timeModel } from '~/models';
import { timeTextsPath } from '~/models/time/selectors';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import styles from './InAppNotificationsDrawer.module.scss';
import EmptyStateIllustration from './InAppNotificationsEmptyStateIllustration.svg';
export const GetInAppNotificationsDrawerTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            InAppNotificationsDrawerTID: {
                Root: 'notification-center',
                Drawer: 'notification-center-drawer',
                EmptyState: 'notification-center-drawer-empty-state',
                ReadItemDot: 'read-item-dot',
                Item: (index)=>"notification-item-".concat(index),
                ItemDescription: (type, index)=>"notification-item-description-".concat(type, "-").concat(index),
                ItemTimeAgo: (index)=>"notification-item-time-ago-".concat(index)
            }
        }
    });
const InAppNotificationsSpotlightBackdrop = (props)=>/*#__PURE__*/ _jsx(Spotlight, {
        spotlightId: NOTIFICATIONS_TRIGGER_SPOTLIGHT_ID,
        open: props.open,
        onClick: props.onClick
    });
export default function InAppNotificationsDrawer() {
    const isMobile = useIsMobileForHomePage();
    const visibleNotifications = useVisibleNotifications();
    const worksAt = useStore(selectors.user.business);
    const email = useStore(selectors.user.email);
    const inAppNotificationsDrawerOpen = useStore((state)=>state.inAppNotificationsDrawerOpen);
    const { api, logBI, fetchInAppNotifications, setInAppNotificationsDrawerOpen } = useStoreActions();
    const { reportToHeap } = useEventReporter();
    const { getDisabled: isIntentDisabled } = useIntentBehavior();
    const { t: drawerTexts } = useLocaleKeys('components.inAppNotificationsDrawer');
    const { t: timeTexts } = useLocaleKeys(timeTextsPath);
    const { getNow } = useIoContext();
    const { InAppNotificationsDrawerTID } = useMemo(()=>GetInAppNotificationsDrawerTID(), []);
    const headerText = {
        title: drawerTexts.title(),
        subtitle: worksAt === null || worksAt === void 0 ? void 0 : worksAt.name
    };
    useEffect(()=>{
        if (inAppNotificationsDrawerOpen && !isIntentDisabled(Intent.ManageNotifications)) {
            const unseenNotificationIds = visibleNotifications.filter((pn)=>pn.id && !pn.seen).map((pn)=>pn.id);
            if ((unseenNotificationIds === null || unseenNotificationIds === void 0 ? void 0 : unseenNotificationIds.length) > 0) {
                api.inAppNotifications.markAsSeen(unseenNotificationIds);
            }
        }
    }, [
        inAppNotificationsDrawerOpen,
        visibleNotifications,
        isIntentDisabled
    ]);
    const content = visibleNotifications.length ? visibleNotifications.map((notificationItem, index)=>{
        var _notificationData_path;
        const notificationData = getNotificationPresentationData(notificationItem, drawerTexts.notifications);
        if (!notificationData) return null;
        const Element = notificationData.path ? NavLink : 'div';
        return /*#__PURE__*/ _jsxs(Element, {
            to: (_notificationData_path = notificationData.path) !== null && _notificationData_path !== void 0 ? _notificationData_path : '',
            id: notificationItem.id,
            "data-testid": InAppNotificationsDrawerTID.Item(index),
            "data-heapid": "notification-item",
            onClick: async ()=>{
                const notificationType = notificationItem[notificationItem.type].type;
                logBI(15001, {
                    notificationType
                });
                reportToHeap('notification-center', 'click', 'vendor-clicked-notification-item', {
                    email,
                    notificationType
                });
                if (notificationData.path) {
                    setInAppNotificationsDrawerOpen(false);
                }
                if (!notificationItem.read && !isIntentDisabled(Intent.ManageNotifications)) {
                    api.inAppNotifications.markAsRead(notificationItem.id);
                    await fetchInAppNotifications();
                }
            },
            className: styles.item,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.icon,
                    children: notificationData.icon
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.main,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": InAppNotificationsDrawerTID.ItemDescription(notificationData.type, index),
                            "data-heapid": "notification-item-description",
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: notificationData.content
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": InAppNotificationsDrawerTID.ItemTimeAgo(index),
                            "data-heapid": "notification-item-time-ago",
                            children: /*#__PURE__*/ _jsx(Typography, {
                                skin: "secondary",
                                variant: "paragraph3",
                                children: timeModel.formatTimeAgo(notificationItem.timestamp, timeTexts, getNow().getTime())
                            })
                        })
                    ]
                }),
                !notificationItem.read && /*#__PURE__*/ _jsx("div", {
                    "data-testid": InAppNotificationsDrawerTID.ReadItemDot(),
                    className: styles.readItemDotContainer,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.readItemDot
                    })
                })
            ]
        }, notificationItem.id);
    }) : /*#__PURE__*/ _jsxs("div", {
        className: styles.emptyState,
        "data-testid": InAppNotificationsDrawerTID.EmptyState(),
        children: [
            /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                children: drawerTexts.emptyTitle()
            }),
            /*#__PURE__*/ _jsx(Typography, {
                children: drawerTexts.emptyMessage()
            })
        ]
    });
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": InAppNotificationsDrawerTID.Root(),
        children: /*#__PURE__*/ _jsx(Drawer, {
            bi: {
                name: 'in_app_notifications_drawer'
            },
            isMobile: isMobile,
            hasTopbar: true,
            header: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "h5",
                        weight: "bold",
                        children: headerText.title
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        skin: "secondary",
                        variant: "paragraph3",
                        children: headerText.subtitle
                    })
                ]
            }),
            open: inAppNotificationsDrawerOpen,
            onClose: ()=>setInAppNotificationsDrawerOpen(false),
            onCloseButtonClick: ()=>setInAppNotificationsDrawerOpen(false),
            testId: InAppNotificationsDrawerTID.Drawer(),
            BackdropComponent: InAppNotificationsSpotlightBackdrop,
            children: content
        })
    });
}
